import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode,
} from "react";
import { useInRouterContext, useLocation, useNavigate } from "react-router-dom";
import { useOptionalSession } from "@core/session/SessionProvider";
import { tokens } from "@core/theme/tokens";
import { portalPrimitiveValues as values } from "./portalPrimitiveValues";
import {
  registerToastNotifier,
  unregisterToastNotifier,
  type ToastTone,
} from "./toastNotifier";
import { CORRELATION_LABEL } from "./errorMessage";
import { copyToClipboard } from "./copyToClipboard";

/** Canonical portal controls extracted from the approved desktop prototype. */
export function PortalButton({
  tone = "primary",
  size = "regular",
  className = "",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  // `danger` se agrega por la revisión en vivo 2026-09-22 (punto #15A): sin un
  // tono de peligro, las confirmaciones destructivas ("Sí, inactivar") tenían
  // que armar el botón rojo a mano con `btn-link`/`btn btn-sm` y el color
  // repetido en cada pantalla, en vez de usar este primitivo. El estilo vive
  // en `.btn-danger` (base.css), con los mismos tokens de peligro del sistema.
  tone?: "primary" | "secondary" | "danger";
  size?: "regular" | "small";
}) {
  const toneClass =
    tone === "primary" ? "btn-primary" : tone === "danger" ? "btn-danger" : "";
  return (
    <button
      {...props}
      className={`btn ${toneClass} ${size === "small" ? "btn-sm" : ""} ${className}`.trim()}
    />
  );
}

export function Badge({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: "neutral" | "silver" | "achievement" | "danger";
}) {
  const skins = {
    neutral: {
      background: tokens.surface.surface,
      borderColor: tokens.surface.line,
      color: tokens.text.tertiary,
    },
    silver: {
      background: tokens.surface.surface2,
      borderColor: tokens.surface.line2,
      color: tokens.silver.flat,
    },
    achievement: {
      background: tokens.gold.dim,
      borderColor: tokens.gold.line,
      color: tokens.gold.light,
    },
    danger: {
      background: tokens.danger.dim,
      borderColor: tokens.danger.line,
      color: tokens.danger.base,
    },
  } as const;
  return (
    <span
      style={{
        display: "inline-flex",
        width: "fit-content",
        padding: values.badge.padding,
        borderRadius: values.badge.radius,
        border: "1px solid",
        font: values.badge.font,
        ...skins[tone],
      }}
    >
      {children}
    </span>
  );
}

/**
 * Pila de diálogos abiertos (`Modal` y `SidePanel` comparten `useDialogFocus`,
 * así que comparten esta pila). Cada instancia se anota al montar y se borra
 * al desmontar; un `Escape` solo cierra la instancia que está más arriba en la
 * pila. Sin esto, dos diálogos anidados (por ejemplo el panel de
 * mantenimientos abierto sobre la ficha del miembro) responden ambos al mismo
 * `Escape` y se cierran juntos en vez de cerrarse uno por vez.
 */
let dialogStack: symbol[] = [];

/**
 * Foco compartido por `Modal` y `SidePanel`: enfoca el panel al montar,
 * cierra con Escape (solo si es el diálogo más externo, ver `dialogStack`) y
 * devuelve el foco al elemento previo al desmontar. Extraído sin cambiar el
 * comportamiento de `Modal` (baseline `portal-foundation-modal-default@1440x900.png`,
 * trece usos en nueve archivos).
 */
function useDialogFocus(onClose?: () => void) {
  const panel = useRef<HTMLDivElement>(null);
  const previous = useRef<HTMLElement | null>(null);
  const idRef = useRef<symbol>();
  if (!idRef.current) idRef.current = Symbol("dialog");
  // QA-MTZ-002 ronda 3 (#2): el efecto dependía de `onClose`, y casi todos
  // los llamadores lo pasan inline (`onClose={() => setX(null)}`). Cada
  // render del padre —por ejemplo, cada tecla en un campo controlado como el
  // motivo de «Cerrar solicitud»— desmontaba y volvía a montar el efecto:
  // el foco saltaba al panel y solo entraba una letra. Se lee la última
  // versión desde una ref y el efecto corre una sola vez por diálogo.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const id = idRef.current!;
    previous.current = document.activeElement as HTMLElement;
    panel.current?.focus();
    dialogStack.push(id);
    const escape = (event: KeyboardEvent) => {
      if (
        event.key === "Escape" &&
        dialogStack[dialogStack.length - 1] === id
      ) {
        onCloseRef.current?.();
      }
    };
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("keydown", escape);
      dialogStack = dialogStack.filter((entry) => entry !== id);
      previous.current?.focus();
    };
  }, []);
  return panel;
}

/**
 * Diálogo modal del portal.
 *
 * Tiene DOS formas, y las dos son válidas (revisión en vivo 2026-09-22, punto
 * #3):
 *
 * 1. Forma histórica — `children` sueltos, sin `header` ni `footer`. El panel
 *    entero es un solo bloque con `overflow-y: auto`: título, contenido y
 *    acciones se desplazan juntos. Es lo que hacen hoy los treinta y tantos
 *    llamadores, y sigue funcionando exactamente igual (mismo DOM, mismo
 *    `padding: 24`) para no pedir una migración de golpe.
 *
 * 2. Forma nueva — con `header` y/o `footer`. El panel se parte en tres
 *    regiones: encabezado fijo, cuerpo con su PROPIO `overflow-y: auto` (y la
 *    barra fina de `.portal-scroll`), y pie fijo. Es la que corrige el pedido
 *    real: que "Nueva recompensa…" y "Cancelar / Guardar y publicar" queden
 *    siempre visibles mientras se desplaza el contenido, y que la barra de
 *    desplazamiento aparezca solo en el contenido.
 *
 * Cómo se adopta la forma nueva en un llamador existente: sacar del `children`
 * el título (y su divisor `border-bottom`, si lo dibujaba a mano) y pasarlo por
 * `header`; sacar la fila de botones (y su `border-top`) y pasarla por
 * `footer`; dejar en `children` solo el contenido. Los divisores y el `padding`
 * de cada región los pone este componente (`values.modalRegions`), así que el
 * llamador borra los suyos en vez de moverlos — si los deja, quedan dos.
 */
export function Modal({
  children,
  label,
  width = 660,
  onClose,
  role = "dialog",
  header,
  footer,
}: {
  children: ReactNode;
  label: string;
  width?: number | string;
  onClose?: () => void;
  role?: "dialog" | "alertdialog";
  header?: ReactNode;
  footer?: ReactNode;
}) {
  const panel = useDialogFocus(onClose);
  const split = header !== undefined || footer !== undefined;
  return (
    <div
      role={role}
      aria-modal="true"
      aria-label={label}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: values.modal.zIndex,
        display: "grid",
        placeItems: "center",
        padding: values.modal.padding,
        background: values.modal.overlay,
        backdropFilter: "blur(2px)",
      }}
    >
      <div
        ref={panel}
        tabIndex={-1}
        style={{
          width,
          maxWidth: "94vw",
          maxHeight: "92vh",
          background: values.modal.surface,
          border: `1px solid ${tokens.surface.line2}`,
          borderRadius: values.modal.radius,
          boxShadow: values.modal.shadow,
          // Sin regiones, el panel entero scrollea (forma histórica). Con
          // regiones, el panel no scrollea nunca: `overflow: hidden` es lo que
          // recorta las esquinas del cuerpo contra el `borderRadius` y deja
          // que el único scroll sea el del cuerpo.
          ...(split
            ? {
                display: "flex",
                flexDirection: "column" as const,
                overflow: "hidden",
              }
            : { overflowY: "auto" as const, padding: values.modal.padding }),
        }}
      >
        {split ? (
          <>
            {header !== undefined ? (
              <div
                style={{
                  flex: "none",
                  padding: values.modalRegions.headerPadding,
                  borderBottom: `1px solid ${tokens.surface.line}`,
                }}
              >
                {header}
              </div>
            ) : null}
            <div
              className="portal-scroll"
              style={{
                // `minHeight: 0` es lo que permite que un hijo flex se encoja
                // por debajo de su contenido: sin él, el cuerpo crece con el
                // contenido, empuja el pie fuera del panel y nunca scrollea.
                flex: 1,
                minHeight: 0,
                // `overflowY` va también en línea, no solo en `.portal-scroll`:
                // el comportamiento (que scrollee) no debe depender de que la
                // hoja de estilos esté cargada; de la clase viene solo la
                // estética fina de la barra.
                overflowY: "auto",
                padding: values.modalRegions.bodyPadding,
              }}
            >
              {children}
            </div>
            {footer !== undefined ? (
              <div
                style={{
                  flex: "none",
                  padding: values.modalRegions.footerPadding,
                  borderTop: `1px solid ${tokens.surface.line}`,
                }}
              >
                {footer}
              </div>
            ) : null}
          </>
        ) : (
          children
        )}
      </div>
    </div>
  );
}

/**
 * Panel lateral de escritorio anclado a la derecha, a altura completa —
 * `Sheet` en el prototipo (admin.jsx:638), renombrado `SidePanel` para
 * distinguirlo del `BottomSheet` móvil de la app (components.md § Colisiones
 * de nombres). Usa la misma composición de foco/Escape que `Modal`, pero no
 * comparte su ancho, fondo ni anclaje: no es una variante de `Modal`, es un
 * primitivo propio.
 */
export function SidePanel({
  children,
  label,
  width = values.sidePanel.width,
  onClose,
}: {
  children: ReactNode;
  label: string;
  width?: number | string;
  onClose?: () => void;
}) {
  const panel = useDialogFocus(onClose);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={label}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: values.sidePanel.zIndex,
      }}
    >
      <div
        onClick={onClose}
        style={{
          position: "absolute",
          inset: 0,
          background: values.sidePanel.overlay,
          backdropFilter: "blur(2px)",
        }}
      />
      <div
        ref={panel}
        tabIndex={-1}
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          width,
          maxWidth: "96vw",
          display: "flex",
          flexDirection: "column",
          background: values.sidePanel.background,
          borderLeft: `1px solid ${tokens.surface.line2}`,
          boxShadow: values.sidePanel.shadow,
        }}
      >
        {children}
      </div>
    </div>
  );
}

export function SegmentedControl({
  options,
  label = "Secciones",
  activeValue,
  onSelect,
}: {
  options: Array<{ value: string; label: string; disabled?: boolean }>;
  label?: string;
  activeValue?: string;
  onSelect?: (value: string) => void;
}) {
  return (
    <nav
      aria-label={label}
      style={{
        display: "inline-flex",
        gap: values.segmented.gap,
        padding: values.segmented.padding,
        borderRadius: values.segmented.radius,
        background: tokens.surface.surface,
        border: `1px solid ${tokens.surface.line}`,
      }}
    >
      {options.map((option) => {
        const active = activeValue === option.value;
        return (
          <button
            key={option.value}
            type="button"
            disabled={option.disabled}
            aria-current={active ? "page" : undefined}
            onClick={() => onSelect?.(option.value)}
            style={{
              padding: values.segmented.optionPadding,
              border: `1px solid ${active ? tokens.surface.line2 : "transparent"}`,
              borderRadius: values.segmented.radius,
              cursor: option.disabled ? "not-allowed" : "pointer",
              background: active ? tokens.surface.surface3 : "transparent",
              color: active ? tokens.text.primary : tokens.text.tertiary,
              font: values.segmented.optionFont,
              letterSpacing: "0.01em",
              whiteSpace: "nowrap",
              opacity: option.disabled ? values.segmented.disabledOpacity : 1,
            }}
          >
            {option.label}
          </button>
        );
      })}
    </nav>
  );
}

const BASE_SECURITY_OPTIONS = [
  { value: "/security/users", label: "Usuarios" },
  { value: "/security/profiles", label: "Perfiles" },
  { value: "/security/access-log", label: "Bitácora de accesos" },
];
// La traza HTTP de diagnóstico es visible solo para el Administrador
// (`session.user.isAdministrator`, no cualquier perfil con `security.view`):
// un perfil distinto no ve el tab, aunque el servidor la rechaza igual si
// llega por URL directa (ADR-011).
const ADMIN_SECURITY_OPTION = {
  value: "/security/http-request-log",
  label: "Traza HTTP",
};
function securityOptions(isAdministrator: boolean) {
  return isAdministrator
    ? [...BASE_SECURITY_OPTIONS, ADMIN_SECURITY_OPTION]
    : BASE_SECURITY_OPTIONS;
}
function RoutedSecuritySegmented() {
  const location = useLocation();
  const navigate = useNavigate();
  const session = useOptionalSession()?.session;
  return (
    <SegmentedControl
      label="Seguridad"
      options={securityOptions(session?.user.isAdministrator ?? false)}
      activeValue={location.pathname}
      onSelect={navigate}
    />
  );
}
export function SecuritySegmented() {
  return useInRouterContext() ? (
    <RoutedSecuritySegmented />
  ) : (
    <SegmentedControl label="Seguridad" options={securityOptions(false)} />
  );
}
export function SecurityHeader() {
  return (
    <header style={{ marginBottom: 19 }}>
      <h1
        style={{
          margin: 0,
          font: `300 34px ${tokens.font.display}`,
          color: tokens.text.primary,
        }}
      >
        Seguridad
      </h1>
      <p
        style={{
          margin: "6px 0 0",
          font: `400 13px ${tokens.font.ui}`,
          color: tokens.text.tertiary,
        }}
      >
        Usuarios del portal, perfiles de acceso y bitácora de ingresos.
      </p>
      <div style={{ marginTop: 23 }}>
        <SecuritySegmented />
      </div>
    </header>
  );
}

// SPEC-208/209/210/211: el prototipo (admin.jsx:2628-2646, AdminPremios) une
// Gran Premio/Recompensas/Beneficios en una sola pantalla con tabs. SPEC-201
// §2 ya reorganizó esa IA: "Recompensas, Beneficios y Ofertas" es su propia
// entrada de navegación (/rewards, nav.ts), separada de "Gestión de
// Premios" (/prizes, con Configuración y Avances y referidos como hijos). Por
// eso este header replica solo el segmentado que sigue vigente
// (Configuración | Avances y referidos). Corrección humana (starcircle-58,
// 2026-09-07): reproducir también la fila de tabs externa del prototipo
// congelado (admin.jsx:2628-2646, AdminPremios) — "Gran Premio" queda en esta
// pantalla (/prizes/configuration); "Recompensas" ya es su propia entrada de
// navegación por SPEC-201 (/rewards); "Avances y referidos" navega a
// /prizes/progress (placeholder del shell de SPEC-201; SPEC-209 lo
// reemplaza). "Beneficios Star Circle" apuntaba también a /rewards mientras
// SPEC-211 no existía; ahora navega a su propia ruta /benefits
// (BenefitsScreen.tsx).
const prizesOptions = [
  { value: "/prizes/configuration", label: "Configuración" },
  { value: "/prizes/progress", label: "Avances y referidos" },
];
function RoutedPrizesSegmented() {
  const location = useLocation();
  const navigate = useNavigate();
  return (
    <SegmentedControl
      label="Gestión de Premios"
      options={prizesOptions}
      activeValue={location.pathname}
      onSelect={navigate}
    />
  );
}
export function PrizesSegmented() {
  return useInRouterContext() ? (
    <RoutedPrizesSegmented />
  ) : (
    <SegmentedControl label="Gestión de Premios" options={prizesOptions} />
  );
}

const prizesTabs = [
  { key: "gran", label: "Gran Premio", to: "/prizes/configuration" },
  { key: "recompensas", label: "Recompensas", to: "/rewards" },
  { key: "beneficios", label: "Beneficios Star Circle", to: "/benefits" },
  // NAV-01 (human-resolution-nav-01-offers-tab.json): SPEC-219 quedaba
  // ruteada pero inalcanzable desde la interfaz sin este cuarto chip — sin
  // reordenar ni tocar los tres anteriores.
  { key: "ofertas", label: "Ofertas", to: "/offers" },
] as const;

// TABS-A (human-resolution-tabs-pattern.json): esta fila es compartida por
// "Gran Premio", "Recompensas", "Beneficios Star Circle" y "Ofertas"
// (SPEC-201/NAV-01), así que el chip activo se deriva de la ruta actual,
// nunca queda fijo en "gran". SPEC-211 agrega su propia ruta /benefits
// (BenefitsScreen.tsx): antes "Beneficios Star Circle" apuntaba al mismo
// /rewards que "Recompensas" porque esa SPEC no existía todavía.
function activePrizesTabKey(
  pathname: string,
): (typeof prizesTabs)[number]["key"] {
  if (pathname.startsWith("/benefits")) return "beneficios";
  if (pathname.startsWith("/rewards")) return "recompensas";
  if (pathname.startsWith("/offers")) return "ofertas";
  return "gran";
}

/** Fila de tabs externa de AdminPremios (admin.jsx:2628-2646). El segmentado
 * "Configuración · Avances y referidos" (`PrizesSegmented`) es subnavegación
 * propia de "Gran Premio": no se dibuja cuando la pestaña activa es
 * "Recompensas" (TABS-A), porque el baseline canónico de Recompensas no lo
 * muestra. */
function RoutedPrizesTabsRow() {
  const navigate = useNavigate();
  const location = useLocation();
  const activeKey = activePrizesTabKey(location.pathname);
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexWrap: "wrap",
        marginBottom: 22,
        paddingBottom: 18,
        borderBottom: `1px solid ${tokens.surface.line}`,
      }}
    >
      {prizesTabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          className={"chip" + (tab.key === activeKey ? " on" : "")}
          style={{ padding: "11px 18px", fontSize: 13 }}
          onClick={() => navigate(tab.to)}
        >
          {tab.label}
        </button>
      ))}
      <div style={{ flex: 1 }} />
      {activeKey === "gran" ? <PrizesSegmented /> : null}
    </div>
  );
}
function PrizesTabsRow() {
  return useInRouterContext() ? (
    <RoutedPrizesTabsRow />
  ) : (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        flexWrap: "wrap",
        marginBottom: 22,
        paddingBottom: 18,
        borderBottom: `1px solid ${tokens.surface.line}`,
      }}
    >
      {prizesTabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          className={"chip" + (tab.key === "gran" ? " on" : "")}
          style={{ padding: "11px 18px", fontSize: 13 }}
        >
          {tab.label}
        </button>
      ))}
      <div style={{ flex: 1 }} />
      <PrizesSegmented />
    </div>
  );
}

export function PrizesHeader() {
  return (
    <header>
      <h1
        style={{
          margin: 0,
          font: `400 34px/1.06 ${tokens.font.display}`,
          color: tokens.text.primary,
        }}
      >
        Gestión de Premios
      </h1>
      <p
        style={{
          margin: "6px 0 0",
          font: `400 13px ${tokens.font.ui}`,
          color: tokens.text.tertiary,
        }}
      >
        Gran premio, recompensas por puntos y beneficios del círculo.
      </p>
      <div style={{ marginTop: 30 }}>
        <PrizesTabsRow />
      </div>
    </header>
  );
}

export type FeedbackKind =
  | "loading"
  | "empty"
  | "filtered-empty"
  | "error"
  | "permission-denied"
  | "success";
export function FeedbackState({
  kind,
  action,
}: {
  kind: FeedbackKind;
  action?: ReactNode;
}) {
  const copy: Record<FeedbackKind, [string, string]> = {
    loading: ["Cargando…", "Estamos preparando la información."],
    empty: [
      "Todavía no hay nada acá",
      "Cuando se cree el primer registro, va a aparecer en esta lista.",
    ],
    "filtered-empty": [
      "El filtro no arroja resultados",
      "Prueba limpiar los filtros activos o cambiar la búsqueda.",
    ],
    error: ["No se pudo cargar la lista", "Intenta de nuevo en unos segundos."],
    "permission-denied": [
      "No tienes acceso a esta sección",
      "Pídele a un administrador que revise tu perfil.",
    ],
    success: ["Acción completada", "Los cambios ya están disponibles."],
  };
  return (
    <div
      role={
        kind === "error" || kind === "permission-denied" ? "alert" : "status"
      }
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: values.feedback.gap,
        padding: values.feedback.padding,
        textAlign: "center",
      }}
    >
      <strong style={{ font: values.feedback.titleFont }}>
        {copy[kind][0]}
      </strong>
      <span
        style={{
          maxWidth: values.feedback.maxWidth,
          color: tokens.text.tertiary,
          font: values.feedback.bodyFont,
        }}
      >
        {copy[kind][1]}
      </span>
      {action}
    </div>
  );
}

export type { ToastTone };

/**
 * Un tono de error no es una cortesía como el de éxito: usa `role="alert"` +
 * `aria-live="assertive"` (el estado previo, `status`/`polite`, es correcto
 * para "success"/"neutral") y dura mucho más (`values.toast.errorDuration`)
 * porque puede traer un identificador de correlación de 36 caracteres que el
 * operador necesita copiar antes de que se descarte solo (T2,
 * qa-bug-matrix-2026-09.md §6).
 */
export function Toast({
  message,
  tone = "success",
  duration,
  correlationId,
  onDismiss,
}: {
  message: string | null;
  tone?: ToastTone;
  duration?: number | undefined;
  correlationId?: string | undefined;
  onDismiss?: () => void;
}) {
  const id = useId();
  const [copied, setCopied] = useState(false);
  const resolvedDuration =
    duration ??
    (tone === "error" ? values.toast.errorDuration : values.toast.duration);
  useEffect(() => {
    if (!message) return;
    const timer = window.setTimeout(() => onDismiss?.(), resolvedDuration);
    return () => window.clearTimeout(timer);
  }, [message, resolvedDuration, onDismiss]);
  useEffect(() => {
    setCopied(false);
  }, [message]);
  if (!message) return null;
  const isError = tone === "error";
  return (
    <div
      id={id}
      role={isError ? "alert" : "status"}
      aria-live={isError ? "assertive" : "polite"}
      style={{
        position: "fixed",
        zIndex: values.toast.zIndex,
        bottom: values.toast.bottom,
        left: "50%",
        transform: "translateX(-50%)",
        display: "flex",
        gap: values.toast.gap,
        alignItems: "center",
        maxWidth: values.toast.maxWidth,
        padding: values.toast.padding,
        borderRadius: values.toast.radius,
        // Revisión en vivo 2026-09-22, punto #17A: `danger.dim` (14%) y
        // `gold.dim` (13%) son casi transparentes, así que el texto de la
        // pantalla se leía A TRAVÉS del toast. Se eligió hacerlo opaco en vez
        // de `backdropFilter: blur(...)` como el overlay del Modal: con un
        // relleno del 13% el desenfoque sigue dejando pasar las manchas claras
        // de lo que hay detrás y el contraste del mensaje queda al azar del
        // fondo. La capa plana del mismo token sobre `surface.bg3` —el fondo
        // sólido que ya usa el tono neutral— da exactamente el mismo tinte sin
        // estrenar ningún color: el tono se conserva, la transparencia no.
        background: isError
          ? `linear-gradient(0deg, ${tokens.danger.dim}, ${tokens.danger.dim}), ${tokens.surface.bg3}`
          : tone === "success"
            ? `linear-gradient(0deg, ${tokens.gold.dim}, ${tokens.gold.dim}), ${tokens.surface.bg3}`
            : tokens.surface.bg3,
        border: `1px solid ${isError ? tokens.danger.line : tone === "success" ? tokens.gold.line : tokens.surface.line2}`,
        color: isError ? tokens.danger.base : tokens.text.primary,
        font: values.toast.font,
        boxShadow: tokens.shadow,
      }}
    >
      {tone === "success" ? "✓" : null}
      {isError ? "⚠" : null}
      <span>{message}</span>
      {isError && correlationId ? (
        <>
          {/* Findings de la ola 1 de `t2-foundation` (intento 2): el mensaje
              ya no embebe el identificador (`errorMessage.ts`), así que este
              chip es la única vez que aparece — con una etiqueta corta,
              porque 36 caracteres sueltos no le dicen a un operador qué son
              ni por qué están ahí. */}
          <span
            style={{
              font: values.toast.correlationFont,
              opacity: 0.85,
              whiteSpace: "nowrap",
            }}
          >
            {CORRELATION_LABEL}:{" "}
            <span style={{ userSelect: "all" }}>{correlationId}</span>
          </span>
          <button
            type="button"
            onClick={() =>
              // Correlación garantizada acá: este botón solo se renderiza
              // dentro del `isError && correlationId ? … : null` de arriba.
              // El identificador ya queda visible y seleccionable arriba, así
              // que `copyToClipboard` no necesita lanzar si el permiso se
              // rechaza o la API no está disponible.
              copyToClipboard(correlationId, setCopied)
            }
            style={{
              flexShrink: 0,
              padding: values.toast.copyButtonPadding,
              borderRadius: values.toast.radius,
              border: `1px solid ${tokens.danger.line}`,
              background: "transparent",
              color: tokens.danger.base,
              font: values.toast.copyButtonFont,
              cursor: "pointer",
            }}
          >
            {copied ? "Copiado" : "Copiar"}
          </button>
        </>
      ) : null}
    </div>
  );
}

type ToastQueueEntry = {
  id: number;
  message: string;
  tone: ToastTone;
  correlationId?: string | undefined;
  duration?: number | undefined;
};
type ToastApi = {
  show: (
    message: string,
    tone?: ToastTone,
    options?: {
      correlationId?: string | undefined;
      duration?: number | undefined;
    },
  ) => void;
  dismiss: () => void;
};
const ToastContext = createContext<ToastApi | null>(null);
export function ToastProvider({ children }: { children: ReactNode }) {
  // Cola, no un único slot: `setToast({...})` pisando el anterior perdía el
  // mensaje de éxito cuando `onSaved` lo emitía junto a un toast de
  // `warning` del servidor — React agrupa ambos `setState` y solo
  // sobrevivía el último (bug #14, T2). Cada entrada se muestra su tiempo
  // completo antes de pasar a la siguiente; ninguna se pierde.
  const [queue, setQueue] = useState<ToastQueueEntry[]>([]);
  const nextId = useRef(0);
  const show = useCallback<ToastApi["show"]>(
    (message, tone = "success", options) => {
      nextId.current += 1;
      setQueue((current) => [
        ...current,
        {
          id: nextId.current,
          message,
          tone,
          correlationId: options?.correlationId,
          duration: options?.duration,
        },
      ]);
    },
    [],
  );
  const dismiss = useCallback(() => {
    setQueue((current) => current.slice(1));
  }, []);
  useEffect(() => {
    registerToastNotifier(show);
    return () => unregisterToastNotifier(show);
  }, [show]);
  const current = queue[0] ?? null;
  const api: ToastApi = { show, dismiss };
  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* `key` por `id` de la cola (revisión en vivo 2026-09-22, punto #17B):
          el temporizador de auto-cierre de `Toast` vive en un efecto que
          depende de `message`. Con un único `<Toast>` sin `key`, dos entradas
          consecutivas con EL MISMO TEXTO no cambiaban `message` al avanzar la
          cola, el efecto no se volvía a disparar, no se programaba temporizador
          nuevo y ese toast quedaba abierto para siempre. Cambiar la `key`
          remonta el componente, así que cada entrada programa el suyo sin
          importar si el texto se repite. */}
      <Toast
        key={current?.id}
        message={current?.message ?? null}
        tone={current?.tone ?? "success"}
        duration={current?.duration}
        correlationId={current?.correlationId}
        onDismiss={dismiss}
      />
    </ToastContext.Provider>
  );
}
export function useToast() {
  const toast = useContext(ToastContext);
  if (!toast) throw new Error("useToast requires ToastProvider");
  return toast;
}
